import { FiGithub, FiMoon, FiSun, FiUser } from "react-icons/fi";
import SafeIcon from "./SafeIcon";
import type { BrandConfig, ConverterTheme } from "./types";

interface HeaderProps {
  brand: BrandConfig;
  darkMode: boolean;
  setDarkMode: (value: boolean) => void;
  theme: ConverterTheme;
}

const Header = ({ brand, darkMode, setDarkMode, theme }: HeaderProps) => (
  <header className="w-full px-4 py-5 md:px-8">
    <div className="mx-auto flex max-w-5xl items-center justify-between">
      <a
        href={brand.href}
        aria-label={`${brand.name} - ${brand.tagline}`}
        className={`flex items-center gap-3 rounded-xl focus-visible:outline-none focus-visible:ring-2 ${theme.focusRing} focus-visible:ring-offset-2 dark:focus-visible:ring-offset-slate-950`}
        translate="no"
      >
        <div
          className={`${theme.logoBox} rounded-2xl p-2.5 text-white shadow-lg`}
        >
          <SafeIcon icon={brand.icon} className="text-xl" />
        </div>
        <div>
          <p
            className={`bg-gradient-to-r bg-clip-text text-xl font-black leading-none tracking-tight text-transparent ${theme.logoText}`}
          >
            {brand.name}
          </p>
          <p className="mt-0.5 text-[10px] font-semibold uppercase tracking-widest text-slate-400">
            {brand.tagline}
          </p>
        </div>
      </a>

      <nav className="flex items-center gap-2" aria-label="Primary">
        <a
          href={brand.repositoryUrl}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`${brand.name} repository on GitHub`}
          title="Repository"
          className={`rounded-full p-2.5 text-slate-600 transition-colors hover:bg-slate-200 hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 ${theme.focusRing} dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-100`}
        >
          <SafeIcon icon={FiGithub} className="text-xl" />
        </a>
        <a
          href={brand.profileUrl}
          target="_blank"
          rel="me noopener noreferrer"
          aria-label="trevonerd GitHub profile"
          title="Profile"
          className={`rounded-full p-2.5 text-slate-600 transition-colors hover:bg-slate-200 hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 ${theme.focusRing} dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-100`}
        >
          <SafeIcon icon={FiUser} className="text-xl" />
        </a>
        <button
          type="button"
          onClick={() => setDarkMode(!darkMode)}
          className={`rounded-full p-2.5 transition-colors hover:bg-slate-200 focus-visible:outline-none focus-visible:ring-2 ${theme.focusRing} dark:hover:bg-slate-800`}
          aria-label={darkMode ? "Switch to light mode" : "Switch to dark mode"}
          title={darkMode ? "Light mode" : "Dark mode"}
        >
          {darkMode ? (
            <SafeIcon icon={FiSun} className="text-xl text-amber-400" />
          ) : (
            <SafeIcon icon={FiMoon} className="text-xl text-slate-600" />
          )}
        </button>
      </nav>
    </div>
  </header>
);

export default Header;
